interface Project {
  id: number;
  name: string;
  description: string;
  categories: string[];
  client?: string;
  year?: number;
  technologies?: string[];
  impact?: string;
  metrics?: {
    value: string;
    description: string;
  }[];
  testimonial?: {
    text: string;
    author: string;
    position: string;
    avatar?: string;
  };
  caseStudy?: string;
  previewImage?: string;
  featured?: boolean;
}

interface Testimonial {
  id: string;
  author: string;
  position: string;
  text: string;
  rating: number;
  projectId?: string;
  date?: string;
}

const projects: Project[] = [
  // Top Tier Projects (Most Impressive)
  {
    id: 1,
    name: "Global Retail Platform",
    description: "Scaled e-commerce solution handling $2B+ annual GMV across 15 countries with 99.99% uptime",
    categories: ["Web", "E-Commerce"],
    client: "Fortune 500 Retailer",
    year: 2023,
    technologies: ["React", "Node.js", "AWS", "Redis"],
    impact: "Increased conversion by 32% and reduced server costs by 40%"
  },
  {
    id: 2,
    name: "National Banking System",
    description: "Core banking platform serving 8M+ customers with real-time transactions and military-grade security",
    categories: ["Web", "Mobile", "Finance"],
    client: "Top 5 US Bank",
    year: 2022,
    technologies: ["Java", "React Native", "Kubernetes"],
    impact: "Processed $12B in first 6 months with zero security incidents"
  },
  {
    id: 3,
    name: "AI Healthcare Diagnostics",
    description: "FDA-approved medical imaging analysis that detects 97% of early-stage cancers (clinical trial results)",
    categories: ["AI", "Healthcare"],
    client: "Medical Research Institute",
    year: 2023,
    technologies: ["Python", "TensorFlow", "DICOM"],
    impact: "Reduced diagnostic time from 2 weeks to 2 minutes"
  },

  // Enterprise Solutions
  {
    id: 4,
    name: "Global Supply Chain Platform",
    description: "Real-time logistics tracking across 200+ warehouses with predictive delay alerts",
    categories: ["Web", "SaaS", "Logistics"],
    client: "Shipping Conglomerate",
    year: 2023,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Reduced delivery delays by 65%"
  },
  {
    id: 5,
    name: "Enterprise CMS",
    description: "Custom content management for 500+ editorial users",
    categories: ["Web", "CMS"],
    client: "MediaCorp",
    year: 2022,
    technologies: ["Next.js", "Strapi"],
    impact: "Improved content publishing efficiency by 40%"
  },
  {
    id: 6,
    name: "Healthcare Portal",
    description: "HIPAA-compliant patient management system",
    categories: ["Web", "Healthcare"],
    client: "MediCare Network",
    year: 2023,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Serves 1M+ patients monthly"
  },
  {
    id: 7,
    name: "Travel Booking System",
    description: "Multi-vendor travel aggregation platform",
    categories: ["Web", "Travel"],
    client: "GlobeTrotter",
    year: 2022,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Processed $100M in bookings in first year"
  },
  {
    id: 8,
    name: "Learning Management System",
    description: "Online education platform with 100k+ users",
    categories: ["Web", "Education"],
    client: "EduTech Solutions",
    year: 2021,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved student engagement by 25%"
  },
  {
    id: 9,
    name: "Real Estate Portal",
    description: "Property listing with VR tours",
    categories: ["Web", "Real Estate"],
    client: "UrbanLiving",
    year: 2023,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Increased property views by 50%"
  },
  {
    id: 10,
    name: "Financial Dashboard",
    description: "Real-time stock market analytics",
    categories: ["Web", "Finance"],
    client: "WealthTrack",
    year: 2023,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved investment decisions by 20%"
  },
  {
    id: 11,
    name: "Event Management System",
    description: "End-to-end event planning platform",
    categories: ["Web", "Events"],
    client: "EventPro",
    year: 2021,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Increased event attendance by 30%"
  },
  {
    id: 12,
    name: "Fitness Membership Portal",
    description: "Gym management with class scheduling",
    categories: ["Web", "Fitness"],
    client: "FitLife",
    year: 2022,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved member retention by 25%"
  },
  {
    id: 13,
    name: "Automotive Marketplace",
    description: "Car dealership inventory system",
    categories: ["Web", "Automotive"],
    client: "AutoTrade",
    year: 2023,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Increased sales by 15%"
  },
  {
    id: 14,
    name: "Music Streaming Platform",
    description: "Custom audio streaming service",
    categories: ["Web", "Music"],
    client: "SoundWave",
    year: 2021,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Grew user base by 50% in first year"
  },

  // High-Traffic Consumer Apps
  {
    id: 15,
    name: "Social Media App",
    description: "Viral video platform scaling to 10M+ MAU with personalized content feeds",
    categories: ["Mobile", "Social"],
    client: "Emerging Media Company",
    year: 2022,
    technologies: ["React Native", "Node.js", "MongoDB", "AWS"],
    impact: "Grew from 0 to 5M users in first year"
  },
  {
    id: 16,
    name: "Banking App",
    description: "Mobile banking with biometric auth",
    categories: ["Mobile", "Finance"],
    client: "First National Bank",
    year: 2023,
    technologies: ["React Native", "Node.js", "MongoDB", "AWS"],
    impact: "Increased mobile banking adoption by 40%"
  },
  {
    id: 17,
    name: "Fitness Tracker",
    description: "Workout companion with wearables",
    categories: ["Mobile", "Health"],
    client: "FitTrack",
    year: 2022,
    technologies: ["React Native", "Node.js", "MongoDB", "AWS"],
    impact: "Improved user engagement by 30%"
  },
  {
    id: 18,
    name: "Social Network",
    description: "Niche community platform",
    categories: ["Mobile", "Social"],
    client: "ConnectMe",
    year: 2021,
    technologies: ["React Native", "Node.js", "MongoDB", "AWS"],
    impact: "Grew user base by 200% in first year"
  },
  {
    id: 19,
    name: "AR Shopping",
    description: "Virtual try-on for retail",
    categories: ["Mobile", "AR"],
    client: "StyleAR",
    year: 2023,
    technologies: ["React Native", "Node.js", "MongoDB", "AWS"],
    impact: "Increased sales by 20%"
  },
  {
    id: 20,
    name: "Language Learning",
    description: "AI-powered language tutor",
    categories: ["Mobile", "Education"],
    client: "LinguaBot",
    year: 2022,
    technologies: ["React Native", "Node.js", "MongoDB", "AWS"],
    impact: "Improved language proficiency by 25%"
  },
  {
    id: 21,
    name: "Ride Sharing",
    description: "Urban mobility solution",
    categories: ["Mobile", "Transport"],
    client: "QuickRide",
    year: 2021,
    technologies: ["React Native", "Node.js", "MongoDB", "AWS"],
    impact: "Reduced carbon emissions by 15%"
  },
  {
    id: 22,
    name: "Medical Diary",
    description: "Health symptom tracker",
    categories: ["Mobile", "Healthcare"],
    client: "HealthLog",
    year: 2023,
    technologies: ["React Native", "Node.js", "MongoDB", "AWS"],
    impact: "Improved patient outcomes by 20%"
  },
  {
    id: 23,
    name: "Photo Editor",
    description: "Professional mobile editing",
    categories: ["Mobile", "Photo"],
    client: "SnapEdit",
    year: 2022,
    technologies: ["React Native", "Node.js", "MongoDB", "AWS"],
    impact: "Increased user engagement by 40%"
  },

  // Specialized Tech
  {
    id: 24,
    name: "Autonomous Vehicle SDK",
    description: "Core perception system for self-driving cars with 99.9% object recognition accuracy",
    categories: ["AI", "Automotive"],
    client: "Auto Manufacturer",
    year: 2023,
    technologies: ["Python", "TensorFlow", "C++", "ROS"],
    impact: "Powering 50k+ vehicles on road today"
  },
  {
    id: 25,
    name: "Predictive Maintenance",
    description: "Industrial equipment monitoring",
    categories: ["AI", "Manufacturing"],
    client: "IndustrialAI",
    year: 2022,
    technologies: ["Python", "TensorFlow", "Node.js", "MongoDB"],
    impact: "Reduced downtime by 30%"
  },
  {
    id: 26,
    name: "Chatbot Platform",
    description: "Enterprise customer service",
    categories: ["AI", "Support"],
    client: "BotFactory",
    year: 2021,
    technologies: ["Python", "TensorFlow", "Node.js", "MongoDB"],
    impact: "Improved customer satisfaction by 25%"
  },
  {
    id: 27,
    name: "Image Recognition",
    description: "Visual search technology",
    categories: ["AI", "Computer Vision"],
    client: "VisuaLook",
    year: 2023,
    technologies: ["Python", "TensorFlow", "OpenCV", "AWS"],
    impact: "Improved search accuracy by 40%"
  },
  {
    id: 28,
    name: "Fraud Detection",
    description: "Financial transaction analysis",
    categories: ["AI", "Security"],
    client: "SecurePay",
    year: 2022,
    technologies: ["Python", "TensorFlow", "Node.js", "MongoDB"],
    impact: "Reduced false positives by 30%"
  },

  // Solid Performers (Still impressive but less flashy)
  {
    id: 29,
    name: "Government Portal",
    description: "Accessible digital services platform meeting WCAG 2.1 AA standards",
    categories: ["Web", "Government"],
    client: "State Government",
    year: 2022,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Serves 2M+ citizens monthly"
  },
  {
    id: 30,
    name: "HR Management",
    description: "Enterprise HR platform",
    categories: ["SaaS", "HR"],
    client: "PeopleFirst",
    year: 2023,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved HR efficiency by 25%"
  },
  {
    id: 31,
    name: "Project Management",
    description: "Team collaboration suite",
    categories: ["SaaS", "Productivity"],
    client: "TaskFlow",
    year: 2022,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved project delivery by 20%"
  },
  {
    id: 32,
    name: "CRM System",
    description: "Sales pipeline management",
    categories: ["SaaS", "Sales"],
    client: "ClientTrack",
    year: 2021,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved sales performance by 15%"
  },
  {
    id: 33,
    name: "Marketing Automation",
    description: "Campaign management",
    categories: ["SaaS", "Marketing"],
    client: "MarketGenius",
    year: 2023,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved marketing ROI by 25%"
  },
  {
    id: 34,
    name: "Accounting Software",
    description: "Cloud-based accounting",
    categories: ["SaaS", "Finance"],
    client: "NumberCrunch",
    year: 2022,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved financial accuracy by 20%"
  },
  {
    id: 35,
    name: "Helpdesk Solution",
    description: "Customer support ticketing",
    categories: ["SaaS", "Support"],
    client: "HelpSwift",
    year: 2021,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved customer satisfaction by 20%"
  },
  {
    id: 36,
    name: "Inventory System",
    description: "Multi-warehouse management",
    categories: ["SaaS", "Logistics"],
    client: "StockWise",
    year: 2023,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved inventory accuracy by 25%"
  },
  {
    id: 37,
    name: "Email Marketing",
    description: "Bulk email campaign tool",
    categories: ["SaaS", "Marketing"],
    client: "MailBoost",
    year: 2022,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved email open rates by 30%"
  },
  {
    id: 38,
    name: "Legal Tech",
    description: "Contract management",
    categories: ["SaaS", "Legal"],
    client: "DocuLegal",
    year: 2021,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved contract review efficiency by 40%"
  },
  {
    id: 39,
    name: "Healthcare SaaS",
    description: "Patient records system",
    categories: ["SaaS", "Healthcare"],
    client: "MediCloud",
    year: 2023,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved patient outcomes by 20%"
  },
  {
    id: 40,
    name: "NFT Marketplace",
    description: "Digital art trading platform",
    categories: ["Blockchain", "Web3"],
    client: "ArtChain",
    year: 2023,
    technologies: ["Solidity", "React", "Node.js", "Ethereum"],
    impact: "Facilitated $10M+ in art sales"
  },
  {
    id: 41,
    name: "Crypto Wallet",
    description: "Multi-chain asset management",
    categories: ["Blockchain", "Finance"],
    client: "CoinVault",
    year: 2022,
    technologies: ["Solidity", "React", "Node.js", "Ethereum"],
    impact: "Secured $100M+ in assets"
  },
  {
    id: 42,
    name: "Smart Contracts",
    description: "Legal agreement automation",
    categories: ["Blockchain", "Legal"],
    client: "ContractChain",
    year: 2021,
    technologies: ["Solidity", "React", "Node.js", "Ethereum"],
    impact: "Improved contract execution efficiency by 30%"
  },
  {
    id: 43,
    name: "Supply Chain",
    description: "Product provenance tracking",
    categories: ["Blockchain", "Logistics"],
    client: "Provenance",
    year: 2023,
    technologies: ["Solidity", "React", "Node.js", "Ethereum"],
    impact: "Improved supply chain transparency by 40%"
  },
  {
    id: 44,
    name: "DAO Platform",
    description: "Decentralized governance",
    categories: ["Blockchain", "Web3"],
    client: "CommunityDAO",
    year: 2022,
    technologies: ["Solidity", "React", "Node.js", "Ethereum"],
    impact: "Facilitated $5M+ in community funding"
  },
  {
    id: 45,
    name: "DeFi Protocol",
    description: "Decentralized lending",
    categories: ["Blockchain", "Finance"],
    client: "OpenFinance",
    year: 2021,
    technologies: ["Solidity", "React", "Node.js", "Ethereum"],
    impact: "Facilitated $20M+ in loans"
  },
  {
    id: 46,
    name: "Job Board",
    description: "Recruitment platform with AI matching",
    categories: ["Web", "HR"],
    client: "CareerConnect",
    year: 2022,
    technologies: ["React", "Python", "MongoDB", "TensorFlow"],
    impact: "Improved job matching accuracy by 25%"
  },
  {
    id: 47,
    name: "Nonprofit Donation Portal",
    description: "Fundraising platform for charities",
    categories: ["Web", "Nonprofit"],
    client: "GiveBack",
    year: 2023,
    technologies: ["Next.js", "Stripe", "Firebase", "Tailwind CSS"],
    impact: "Facilitated $1M+ in donations"
  },
  {
    id: 48,
    name: "Legal Document System",
    description: "Secure document management for law firms",
    categories: ["Web", "Legal"],
    client: "LawSecure",
    year: 2022,
    technologies: ["React", "Node.js", "MongoDB", "AWS"],
    impact: "Improved document security by 40%"
  },
  {
    id: 49,
    name: "Gaming Platform",
    description: "Mobile esports tournament",
    categories: ["Mobile", "Gaming"],
    client: "GameOn",
    year: 2021,
    technologies: ["React Native", "Node.js", "MongoDB", "AWS"],
    impact: "Hosted 100k+ tournaments"
  },
  {
    id: 50,
    name: "Parenting App",
    description: "Child development tracker",
    categories: ["Mobile", "Family"],
    client: "ParentPro",
    year: 2023,
    technologies: ["React Native", "Node.js", "MongoDB", "AWS"],
    impact: "Improved parenting engagement by 30%"
  },
  {
    id: 51,
    name: "News Aggregator",
    description: "Personalized news feed",
    categories: ["Mobile", "Media"],
    client: "NewsHive",
    year: 2022,
    technologies: ["React Native", "Node.js", "MongoDB", "AWS"],
    impact: "Improved user engagement by 25%"
  }
];

const testimonials: Testimonial[] = [
  {
    id: "testimonial-1",
    text: "Their development team delivered our e-commerce platform 3 weeks ahead of schedule, resulting in 40% higher conversion rates than projected.",
    author: "Emma Rodriguez",
    position: "Director of Digital, FashionForward",
    rating: 5,
    projectId: "project-3"
  },
  {
    id: "testimonial-2",
    text: "The AI recommendation engine they built increased our average order value by 28% in the first quarter post-launch. Exceptional results.",
    author: "James Chen",
    position: "CTO, TechNova",
    rating: 5,
    projectId: "project-7"
  },
  {
    id: "testimonial-3",
    text: "As a healthcare provider, security was critical. Their team implemented HIPAA-compliant solutions that passed our audit with zero findings.",
    author: "Dr. Sarah Johnson",
    position: "CIO, MetroHealth",
    rating: 5,
    projectId: "project-12"
  },
  {
    id: "testimonial-4",
    text: "The mobile app they developed now handles 90% of our customer transactions. User ratings jumped from 3.8 to 4.7 stars after the redesign.",
    author: "Michael Patel",
    position: "VP Product, Finova",
    rating: 5,
    projectId: "project-5"
  },
  {
    id: "testimonial-5",
    text: "Working with them was transformative. They didn't just build what we asked for - they identified opportunities we hadn't considered.",
    author: "Lisa Wong",
    position: "CEO, EduTech Solutions",
    rating: 5,
    projectId: "project-9"
  }
];

export { projects, testimonials };
export type { Project, Testimonial };